import React, { useState, useEffect } from 'react';
import { Plus, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CartItem } from '@/contexts/CartContext';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface CartItemCardProps {
  item: CartItem;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  onPriceChange?: (itemId: string, newPrice: number) => void;
}

export const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  onUpdateQuantity,
  onRemove,
  onPriceChange
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousPrice, setPreviousPrice] = useState(item.price);

  // Track price changes for animations
  useEffect(() => {
    if (item.price !== previousPrice) {
      setIsAnimating(true);
      onPriceChange?.(item.id, item.price * item.quantity);
      
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setPreviousPrice(item.price);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [item.price, previousPrice, item.id, item.quantity, onPriceChange]);

  const totalItemPrice = item.price * item.quantity;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, x: -100 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="border border-border hover:shadow-md transition-shadow duration-200">
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm truncate">{item.title}</h4>
              {item.description && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {item.description}
                </p>
              )}
              
              <div className="flex items-center justify-between mt-3">
                {/* Quantity Controls */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 rounded-full hover:bg-accent transition-colors duration-200"
                    onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  
                  <motion.span
                    key={item.quantity}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    className="text-sm font-medium w-6 text-center"
                  >
                    {item.quantity}
                  </motion.span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 rounded-full hover:bg-accent transition-colors duration-200"
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>

                {/* Price Display */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={totalItemPrice}
                    initial={{ scale: 1.1, color: 'rgb(16, 185, 129)' }}
                    animate={{ scale: 1, color: 'hsl(var(--primary))' }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "text-sm font-medium",
                      isAnimating && "text-green-500"
                    )}
                  >
                    {totalItemPrice} CHF
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Unit Price (shown when quantity > 1) */}
              {item.quantity > 1 && (
                <div className="text-xs text-muted-foreground mt-1">
                  {item.price} CHF × {item.quantity}
                </div>
              )}
            </div>

            {/* Remove Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(item.id)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 ml-2 rounded-full flex-shrink-0 transition-all duration-200"
              title="Eliminar del carrito"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};